// src/massiveRecruitment/MassiveRecruitmentForm.js
import { useState, useEffect } from 'react';
import {
  Box, Typography, Button, TextField, Tabs, Tab, Paper, Grid,
  FormControlLabel, Switch, Autocomplete, CircularProgress, Alert,
  Chip, Divider, ToggleButtonGroup, ToggleButton, InputAdornment,
  Breadcrumbs, Link,
} from '@mui/material';
import {
  Save as SaveIcon, ArrowBack as ArrowBackIcon,
  Info as InfoIcon, LocationOn as LocationIcon,
  Campaign as CampaignIcon, AccountBalance as BudgetIcon,
  Timeline as StagesIcon, People as PeopleIcon, Home as HomeIcon,
} from '@mui/icons-material';
import api from '../config/api';
import useStore from '../config/store';
import { useTranslation } from 'react-i18next';

const ETAPAS_CATALOGO = [
  { id: 'publicacion',               nombre: 'Publicación de vacante'    },
  { id: 'revision_antecedentes',     nombre: 'Revisión de antecedentes'  },
  { id: 'validacion_documentacion',  nombre: 'Validación documentación'  },
  { id: 'contacto_whatsapp',         nombre: 'Contacto WhatsApp'         },
  { id: 'screening_telefonico',      nombre: 'Screening telefónico'      },
  { id: 'entrevista_grafton',        nombre: 'Entrevista Grafton'        },
  { id: 'entrevista_cliente',        nombre: 'Entrevista con cliente'    },
  { id: 'examenes_preocupacionales', nombre: 'Exámenes preocupacionales' },
  { id: 'contratacion',              nombre: 'Contratación'              },
];

const ETAPAS_ACTIVAS_DEFAULT = ['publicacion', 'revision_antecedentes', 'contacto_whatsapp', 'screening_telefonico', 'entrevista_grafton', 'contratacion'];

const ETAPAS_DEFAULT = ETAPAS_CATALOGO.map((e, i) => ({
  ...e,
  activa: ETAPAS_ACTIVAS_DEFAULT.includes(e.id),
  orden: i + 1,
}));

const CANALES_OPCIONES = ['portales_empleo', 'instagram', 'linkedin', 'referidos', 'whatsapp', 'otro'];
const PRIORIDADES      = ['alta', 'media', 'baja'];
const MODALIDADES      = ['presencial', 'remoto', 'híbrido'];
const TIPOS_CONTRATO   = ['indefinido', 'plazo_fijo', 'proyecto', 'honorarios'];
const JORNADAS         = ['full-time', 'part-time', 'turno'];
const MONEDAS          = ['CLP', 'USD', 'ARS'];
const TODOS_ESTADOS    = ['borrador', 'activo', 'pausado', 'cerrado', 'cubierto', 'cancelado'];

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const FORM_EMPTY = {
  nombre: '',
  descripcionProceso: '',
  positionId: '',
  positionObj: null,
  vacantesRequeridas: '',
  prioridad: 'media',
  estado: 'borrador',
  modalidad: 'presencial',
  ubicacion: { pais: '', ciudad: '', comuna: '', direccion: '' },
  tipoContrato: 'indefinido',
  jornada: 'full-time',
  condicionesLaborales: { horario: '', comunaLugarTrabajo: '', tieneAcercamiento: false, tieneAlmuerzo: false },
  canalesPublicacion: [],
  fechaInicioBusqueda: '',
  fechaCierreBusqueda: '',
  aceptaExtranjeros: false,
  presupuesto: { total: '', salarioPorPersona: '', moneda: 'CLP' },
  etapas: ETAPAS_DEFAULT,
  selectedApplicants: [],
};

const MassiveRecruitmentForm = ({ mode = 'create', processId = null, onSuccess, onCancel }) => {
  const { t } = useTranslation();
  const { authToken, project, organization, showAlert } = useStore();

  const [tabValue, setTabValue]         = useState(0);
  const [saving, setSaving]             = useState(false);
  const [loadingData, setLoadingData]   = useState(mode === 'edit');
  const [loadingPositions, setLoadingPositions]   = useState(false);
  const [loadingApplicants, setLoadingApplicants] = useState(false);
  const [positions, setPositions]   = useState([]);
  const [applicants, setApplicants] = useState([]);
  const [errors, setErrors]         = useState({});
  const [formData, setFormData]     = useState(FORM_EMPTY);

  const isEdit = mode === 'edit';

  // --- Cargar proceso existente ---
  const loadProcess = async () => {
    try {
      setLoadingData(true);
      const response = await api.get(`/massive-recruitments/${processId}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (response.data.response?.status_response === 'OK') {
        const p = response.data.response.data;
        const toDateStr = (val) => {
          if (!val) return '';
          try {
            const d = val._seconds ? new Date(val._seconds * 1000) : new Date(val);
            return d.toISOString().split('T')[0];
          } catch { return ''; }
        };
        setFormData({
          nombre:              p.nombre || '',
          descripcionProceso:  p.descripcionProceso || '',
          positionId:          p.positionId || '',
          positionObj:         null,
          vacantesRequeridas:  p.vacantesRequeridas ?? '',
          prioridad:           p.prioridad || 'media',
          estado:              p.estado || 'borrador',
          modalidad:           p.modalidad || 'presencial',
          ubicacion:           p.ubicacion || { pais: '', ciudad: '', comuna: '', direccion: '' },
          tipoContrato:        p.tipoContrato || 'indefinido',
          jornada:             p.jornada || 'full-time',
          condicionesLaborales: p.condicionesLaborales || { horario: '', comunaLugarTrabajo: '', tieneAcercamiento: false, tieneAlmuerzo: false },
          canalesPublicacion:  p.canalesPublicacion || [],
          fechaInicioBusqueda: toDateStr(p.fechaInicioBusqueda),
          fechaCierreBusqueda: toDateStr(p.fechaCierreBusqueda),
          aceptaExtranjeros:   p.aceptaExtranjeros || false,
          presupuesto: {
            total:             p.presupuesto?.total ?? '',
            salarioPorPersona: p.presupuesto?.salarioPorPersona ?? '',
            moneda:            p.presupuesto?.moneda || 'CLP',
          },
          etapas:              p.etapas?.length ? p.etapas : ETAPAS_DEFAULT,
          selectedApplicants:  [],
        });
      } else {
        showAlert(t('generalApiError'), 'error');
        onCancel();
      }
    } catch (err) {
      showAlert(t('generalApiError'), 'error');
      console.error('Error loading process for edit:', err);
      onCancel();
    } finally {
      setLoadingData(false);
    }
  };

  const loadPositions = async () => {
    try {
      setLoadingPositions(true);
      const response = await api.get(`/positions`, {
        params: { projectId: project },
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (response.data.response?.status_response === 'OK') {
        setPositions(response.data.response.data || []);
      }
    } catch (err) {
      console.error('Error loading positions:', err);
    } finally {
      setLoadingPositions(false);
    }
  };

  const loadApplicants = async () => {
    try {
      setLoadingApplicants(true);
      const response = await api.get(`/applicants`, {
        params: { projectId: project },
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (response.data.response?.status_response === 'OK') {
        setApplicants(response.data.response.data || []);
      }
    } catch (err) {
      console.error('Error loading applicants:', err);
    } finally {
      setLoadingApplicants(false);
    }
  };

  useEffect(() => {
    if (project) {
      loadPositions();
      loadApplicants();
    }
  }, [project]);

  // Resolver positionObj cuando cargan positions en modo edit
  useEffect(() => {
    if (isEdit && positions.length > 0 && formData.positionId && !formData.positionObj) {
      const found = positions.find((p) => p.uid === formData.positionId);
      if (found) setFormData((prev) => ({ ...prev, positionObj: found }));
    }
  }, [positions, isEdit]);

  useEffect(() => {
    if (isEdit && processId) loadProcess();
  }, [isEdit, processId]);

  // --- Handlers ---
  const handleField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: null }));
  };

  const handleNestedField = (parent, field, value) => {
    setFormData((prev) => ({ ...prev, [parent]: { ...prev[parent], [field]: value } }));
  };

  const handleToggleCanal = (canal) => {
    setFormData((prev) => {
      const current = prev.canalesPublicacion;
      return {
        ...prev,
        canalesPublicacion: current.includes(canal)
          ? current.filter((c) => c !== canal)
          : [...current, canal],
      };
    });
  };

  const handleToggleEtapa = (etapaId) => {
    setFormData((prev) => ({
      ...prev,
      etapas: prev.etapas.map((e) => e.id === etapaId ? { ...e, activa: !e.activa } : e),
    }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.nombre.trim())
      newErrors.nombre = t('massiveRecruitment.form.errors.nombre');
    if (!formData.positionId)
      newErrors.positionId = t('massiveRecruitment.form.errors.position');
    if (!formData.vacantesRequeridas || Number(formData.vacantesRequeridas) <= 0)
      newErrors.vacantesRequeridas = t('massiveRecruitment.form.errors.vacantes');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const buildPayload = () => ({
    nombre:              formData.nombre.trim(),
    descripcionProceso:  formData.descripcionProceso.trim(),
    positionId:          formData.positionId,
    vacantesRequeridas:  Number(formData.vacantesRequeridas),
    prioridad:           formData.prioridad,
    estado:              formData.estado,
    modalidad:           formData.modalidad,
    ubicacion:           formData.ubicacion,
    tipoContrato:        formData.tipoContrato,
    jornada:             formData.jornada,
    condicionesLaborales: formData.condicionesLaborales,
    canalesPublicacion:  formData.canalesPublicacion,
    fechaInicioBusqueda: formData.fechaInicioBusqueda || null,
    fechaCierreBusqueda: formData.fechaCierreBusqueda || null,
    aceptaExtranjeros:   formData.aceptaExtranjeros,
    presupuesto: {
      total:             Number(formData.presupuesto.total) || 0,
      salarioPorPersona: Number(formData.presupuesto.salarioPorPersona) || 0,
      moneda:            formData.presupuesto.moneda,
    },
  });

  const handleCreate = async () => {
    const payload = { ...buildPayload(), projectId: project, organizationId: organization };
    const response = await api.post(`/massive-recruitments`, payload, {
      headers: { Authorization: `Bearer ${authToken}` },
    });
    if (response.data.response?.status_response === 'OK') {
      const newId = response.data.response.data?.uid;
      await api.put(`/massive-recruitments/${newId}/etapas`, { etapas: formData.etapas }, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (formData.selectedApplicants.length > 0) {
        await api.post(`/massive-recruitments/${newId}/applicants/bulk`,
          { applicantIds: formData.selectedApplicants.map((a) => a.uid || a.id) },
          { headers: { Authorization: `Bearer ${authToken}` } }
        );
      }
      showAlert(t('massiveRecruitment.form.createSuccess'), 'success');
      onSuccess();
    } else {
      showAlert(response.data.response?.message || t('generalApiError'), 'error');
    }
  };

  const handleUpdate = async () => {
    const response = await api.put(`/massive-recruitments/${processId}`, buildPayload(), {
      headers: { Authorization: `Bearer ${authToken}` },
    });
    if (response.data.response?.status_response !== 'OK') {
      showAlert(response.data.response?.message || t('generalApiError'), 'error');
      return;
    }
    await api.put(`/massive-recruitments/${processId}/etapas`, { etapas: formData.etapas }, {
      headers: { Authorization: `Bearer ${authToken}` },
    });
    if (formData.selectedApplicants.length > 0) {
      await api.post(`/massive-recruitments/${processId}/applicants/bulk`,
        { applicantIds: formData.selectedApplicants.map((a) => a.uid || a.id) },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
    }
    showAlert(t('massiveRecruitment.form.updateSuccess'), 'success');
    onSuccess();
  };

  const handleSubmit = async () => {
    if (!validate()) {
      setTabValue(0);
      showAlert(t('massiveRecruitment.form.validationError'), 'warning');
      return;
    }
    try {
      setSaving(true);
      if (isEdit) { await handleUpdate(); } else { await handleCreate(); }
    } catch (err) {
      showAlert(t('generalApiError'), 'error');
      console.error('Error saving massive recruitment:', err);
    } finally {
      setSaving(false);
    }
  };

  if (loadingData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Breadcrumb modo edición */}
      {isEdit && (
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link component="button" variant="body1" onClick={onCancel} sx={{ display: 'flex', alignItems: 'center' }}>
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            {t('massiveRecruitment.title')}
          </Link>
          <Typography color="text.primary">
            {formData.nombre || t('massiveRecruitment.form.editTitle')}
          </Typography>
        </Breadcrumbs>
      )}

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">
          {isEdit
            ? `${t('massiveRecruitment.form.editTitle')}: ${formData.nombre}`
            : t('massiveRecruitment.form.title')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={onCancel} disabled={saving}>
            {t('cancel')}
          </Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
            onClick={handleSubmit}
            disabled={saving}
          >
            {t('save')}
          </Button>
        </Box>
      </Box>

      <Paper elevation={1}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} variant="scrollable" scrollButtons="auto">
            <Tab icon={<InfoIcon />}     iconPosition="start" label={t('massiveRecruitment.form.tabs.general')} />
            <Tab icon={<LocationIcon />} iconPosition="start" label={t('massiveRecruitment.form.tabs.logistics')} />
            <Tab icon={<CampaignIcon />} iconPosition="start" label={t('massiveRecruitment.form.tabs.publication')} />
            <Tab icon={<BudgetIcon />}   iconPosition="start" label={t('massiveRecruitment.form.tabs.budget')} />
            <Tab icon={<StagesIcon />}   iconPosition="start" label={t('massiveRecruitment.form.tabs.stages')} />
            <Tab icon={<PeopleIcon />}   iconPosition="start" label={t('massiveRecruitment.form.tabs.applicants')} />
          </Tabs>
        </Box>

        <Box sx={{ p: 3 }}>

          {/* ===== TAB 0 — GENERAL ===== */}
          <TabPanel value={tabValue} index={0}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  label={t('massiveRecruitment.form.nombre')}
                  value={formData.nombre}
                  onChange={(e) => handleField('nombre', e.target.value)}
                  fullWidth required
                  error={!!errors.nombre}
                  helperText={errors.nombre || t('massiveRecruitment.form.nombreHelper')}
                  placeholder={t('massiveRecruitment.form.nombrePlaceholder')}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={t('massiveRecruitment.form.descripcion')}
                  value={formData.descripcionProceso}
                  onChange={(e) => handleField('descripcionProceso', e.target.value)}
                  fullWidth multiline rows={3}
                  placeholder={t('massiveRecruitment.form.descripcionPlaceholder')}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={positions}
                  getOptionLabel={(o) => `${o.name}${o.industria ? ' — ' + o.industria : ''}`}
                  value={formData.positionObj}
                  onChange={(_, newValue) => {
                    handleField('positionObj', newValue);
                    handleField('positionId', newValue?.uid || '');
                  }}
                  loading={loadingPositions}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('massiveRecruitment.form.position')}
                      required
                      error={!!errors.positionId}
                      helperText={errors.positionId}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (<>{loadingPositions && <CircularProgress size={18} />}{params.InputProps.endAdornment}</>),
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label={t('massiveRecruitment.form.vacantesRequeridas')}
                  value={formData.vacantesRequeridas}
                  onChange={(e) => handleField('vacantesRequeridas', e.target.value)}
                  fullWidth required type="number" inputProps={{ min: 1 }}
                  error={!!errors.vacantesRequeridas}
                  helperText={errors.vacantesRequeridas}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t('massiveRecruitment.form.prioridad')}
                </Typography>
                <ToggleButtonGroup
                  value={formData.prioridad} exclusive size="small" fullWidth
                  onChange={(_, val) => { if (val) handleField('prioridad', val); }}
                >
                  {PRIORIDADES.map((p) => (
                    <ToggleButton key={p} value={p}>
                      {t(`massiveRecruitment.form.prioridades.${p}`)}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t('massiveRecruitment.form.estado')}
                </Typography>
                <ToggleButtonGroup
                  value={formData.estado} exclusive size="small"
                  onChange={(_, val) => { if (val) handleField('estado', val); }}
                >
                  {TODOS_ESTADOS.map((e) => (
                    <ToggleButton key={e} value={e}>
                      {t(`massiveRecruitment.estados.${e}`)}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  {t('massiveRecruitment.form.estadoHelper')}
                </Typography>
              </Grid>
            </Grid>
          </TabPanel>

          {/* ===== TAB 1 — LOGÍSTICA ===== */}
          <TabPanel value={tabValue} index={1}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t('massiveRecruitment.form.modalidad')}
                </Typography>
                <ToggleButtonGroup
                  value={formData.modalidad} exclusive size="small"
                  onChange={(_, val) => { if (val) handleField('modalidad', val); }}
                >
                  {MODALIDADES.map((m) => (
                    <ToggleButton key={m} value={m}>
                      {t(`massiveRecruitment.form.modalidades.${m}`)}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2">{t('massiveRecruitment.form.ubicacion')}</Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField label={t('massiveRecruitment.form.pais')} value={formData.ubicacion.pais}
                  onChange={(e) => handleNestedField('ubicacion', 'pais', e.target.value)} fullWidth />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField label={t('massiveRecruitment.form.ciudad')} value={formData.ubicacion.ciudad}
                  onChange={(e) => handleNestedField('ubicacion', 'ciudad', e.target.value)} fullWidth />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField label={t('massiveRecruitment.form.comuna')} value={formData.ubicacion.comuna}
                  onChange={(e) => handleNestedField('ubicacion', 'comuna', e.target.value)} fullWidth />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField label={t('massiveRecruitment.form.direccion')} value={formData.ubicacion.direccion}
                  onChange={(e) => handleNestedField('ubicacion', 'direccion', e.target.value)} fullWidth />
              </Grid>
              <Grid item xs={12}><Divider /></Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t('massiveRecruitment.form.tipoContrato')}
                </Typography>
                <ToggleButtonGroup
                  value={formData.tipoContrato} exclusive size="small"
                  onChange={(_, val) => { if (val) handleField('tipoContrato', val); }}
                >
                  {TIPOS_CONTRATO.map((tc) => (
                    <ToggleButton key={tc} value={tc}>
                      {t(`massiveRecruitment.form.tiposContrato.${tc}`)}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t('massiveRecruitment.form.jornada')}
                </Typography>
                <ToggleButtonGroup
                  value={formData.jornada} exclusive size="small"
                  onChange={(_, val) => { if (val) handleField('jornada', val); }}
                >
                  {JORNADAS.map((j) => (
                    <ToggleButton key={j} value={j}>
                      {t(`massiveRecruitment.form.jornadas.${j}`)}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Grid>
              <Grid item xs={12}><Divider /></Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2">{t('massiveRecruitment.form.condicionesLaborales')}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('massiveRecruitment.form.horario')}
                  value={formData.condicionesLaborales.horario}
                  onChange={(e) => handleNestedField('condicionesLaborales', 'horario', e.target.value)}
                  fullWidth placeholder="Ej: Lunes a viernes 08:00 - 18:00"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('massiveRecruitment.form.comunaLugarTrabajo')}
                  value={formData.condicionesLaborales.comunaLugarTrabajo}
                  onChange={(e) => handleNestedField('condicionesLaborales', 'comunaLugarTrabajo', e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControlLabel
                  control={<Switch checked={formData.condicionesLaborales.tieneAcercamiento}
                    onChange={(e) => handleNestedField('condicionesLaborales', 'tieneAcercamiento', e.target.checked)} />}
                  label={t('massiveRecruitment.form.tieneAcercamiento')}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControlLabel
                  control={<Switch checked={formData.condicionesLaborales.tieneAlmuerzo}
                    onChange={(e) => handleNestedField('condicionesLaborales', 'tieneAlmuerzo', e.target.checked)} />}
                  label={t('massiveRecruitment.form.tieneAlmuerzo')}
                />
              </Grid>
            </Grid>
          </TabPanel>

          {/* ===== TAB 2 — PUBLICACIÓN ===== */}
          <TabPanel value={tabValue} index={2}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Switch checked={formData.aceptaExtranjeros}
                    onChange={(e) => handleField('aceptaExtranjeros', e.target.checked)} />}
                  label={t('massiveRecruitment.form.aceptaExtranjeros')}
                />
              </Grid>
              <Grid item xs={12}><Divider /></Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 2 }}>
                  {t('massiveRecruitment.form.canalesPublicacion')}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {CANALES_OPCIONES.map((canal) => {
                    const active = formData.canalesPublicacion.includes(canal);
                    return (
                      <Chip
                        key={canal}
                        label={t(`massiveRecruitment.form.canales.${canal}`)}
                        onClick={() => handleToggleCanal(canal)}
                        color={active ? 'primary' : 'default'}
                        variant={active ? 'filled' : 'outlined'}
                        clickable
                      />
                    );
                  })}
                </Box>
              </Grid>
              <Grid item xs={12}><Divider /></Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('massiveRecruitment.form.fechaInicio')}
                  type="date" value={formData.fechaInicioBusqueda}
                  onChange={(e) => handleField('fechaInicioBusqueda', e.target.value)}
                  fullWidth InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('massiveRecruitment.form.fechaCierre')}
                  type="date" value={formData.fechaCierreBusqueda}
                  onChange={(e) => handleField('fechaCierreBusqueda', e.target.value)}
                  fullWidth InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </TabPanel>

          {/* ===== TAB 3 — PRESUPUESTO ===== */}
          <TabPanel value={tabValue} index={3}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t('massiveRecruitment.form.moneda')}
                </Typography>
                <ToggleButtonGroup
                  value={formData.presupuesto.moneda} exclusive size="small"
                  onChange={(_, val) => { if (val) handleNestedField('presupuesto', 'moneda', val); }}
                >
                  {MONEDAS.map((m) => (
                    <ToggleButton key={m} value={m}>{m}</ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('massiveRecruitment.form.presupuestoTotal')}
                  value={formData.presupuesto.total}
                  onChange={(e) => handleNestedField('presupuesto', 'total', e.target.value)}
                  fullWidth type="number" inputProps={{ min: 0 }}
                  InputProps={{ startAdornment: <InputAdornment position="start">{formData.presupuesto.moneda}</InputAdornment> }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('massiveRecruitment.form.salarioPorPersona')}
                  value={formData.presupuesto.salarioPorPersona}
                  onChange={(e) => handleNestedField('presupuesto', 'salarioPorPersona', e.target.value)}
                  fullWidth type="number" inputProps={{ min: 0 }}
                  InputProps={{ startAdornment: <InputAdornment position="start">{formData.presupuesto.moneda}</InputAdornment> }}
                />
              </Grid>
            </Grid>
          </TabPanel>

          {/* ===== TAB 4 — ETAPAS ===== */}
          <TabPanel value={tabValue} index={4}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('massiveRecruitment.form.stagesHelper')}
            </Typography>
            <Grid container spacing={2}>
              {formData.etapas.map((etapa) => (
                <Grid item xs={12} md={6} key={etapa.id}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      borderColor: etapa.activa ? 'primary.main' : 'divider',
                      backgroundColor: etapa.activa ? 'primary.50' : 'transparent',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Chip
                        label={etapa.orden} size="small"
                        color={etapa.activa ? 'primary' : 'default'}
                        sx={{ width: 32, height: 32, borderRadius: '50%' }}
                      />
                      <Typography variant="body2" fontWeight={etapa.activa ? 'medium' : 'normal'}>
                        {etapa.nombre}
                      </Typography>
                    </Box>
                    <Switch
                      checked={etapa.activa}
                      onChange={() => handleToggleEtapa(etapa.id)}
                      size="small"
                    />
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </TabPanel>

          {/* ===== TAB 5 — POSTULANTES ===== */}
          <TabPanel value={tabValue} index={5}>
            <Alert severity="info" sx={{ mb: 3 }}>
              {isEdit
                ? t('massiveRecruitment.form.applicantsHelperEdit')
                : t('massiveRecruitment.form.applicantsHelper')}
            </Alert>
            <Autocomplete
              multiple
              options={applicants}
              getOptionLabel={(o) => `${o.name}${o.email ? ' — ' + o.email : ''}`}
              value={formData.selectedApplicants}
              onChange={(_, newValue) => handleField('selectedApplicants', newValue)}
              loading={loadingApplicants}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    label={option.name}
                    size="small"
                    {...getTagProps({ index })}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('massiveRecruitment.form.selectApplicants')}
                  placeholder={t('massiveRecruitment.form.searchApplicants')}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (<>{loadingApplicants && <CircularProgress size={18} />}{params.InputProps.endAdornment}</>),
                  }}
                />
              )}
            />
            {formData.selectedApplicants.length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                {formData.selectedApplicants.length} {t('massiveRecruitment.form.applicantsSelected')}
              </Typography>
            )}
          </TabPanel>

        </Box>
      </Paper>

      {/* Botones inferiores */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
        <Button variant="outlined" onClick={onCancel} disabled={saving}>{t('cancel')}</Button>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
          onClick={handleSubmit}
          disabled={saving}
        >
          {t('save')}
        </Button>
      </Box>
    </Box>
  );
};

export default MassiveRecruitmentForm;